import React from "react";

const LINK_RE = /(\[[^\]]+\]\((?:https?:\/\/|\/|mailto:|tel:)[^)\s]+\))/g;

const inline = (text) =>
  text.split(LINK_RE).flatMap((chunk, j) => {
    const m = chunk.match(/^\[([^\]]+)\]\(([^)\s]+)\)$/);
    if (m) {
      const external = /^https?:\/\//.test(m[2]);
      return [<a key={`l${j}`} href={m[2]} target={external ? "_blank" : undefined} rel={external ? "noopener noreferrer" : undefined} className="font-semibold text-[#FF6B9D] underline decoration-2 underline-offset-2 hover:text-[#19324A]">{m[1]}</a>];
    }
    return chunk.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g).map((part, i) => {
      if (part.startsWith("**")) return <strong key={`${j}-${i}`}>{part.slice(2, -2)}</strong>;
      if (part.startsWith("*") && part.length > 2) return <em key={`${j}-${i}`}>{part.slice(1, -1)}</em>;
      return part;
    });
  });

export default function RichText({ text = "", className = "" }) {
  const blocks = text.replace(/\r/g, "").split(/\n\s*\n/).map((b) => b.trim()).filter(Boolean);
  return (
    <div className={`space-y-5 ${className}`} data-testid="rich-text">
      {blocks.map((b, i) => {
        const vid = b.match(/^!video\[([^\]]*)\]\(([^)\s]+)\)(?:\(([^)\s]+)\))?(?:\n([\s\S]*))?$/);
        if (vid) return (
          <figure key={i} className="my-2" data-testid={`rich-video-${i}`}>
            <video src={vid[2]} poster={vid[3] || undefined} controls playsInline preload="none"
              className="block w-full max-w-sm mx-auto h-auto aspect-[9/16] object-contain rounded-3xl soft-shadow bg-[#19324A]" />
            {(vid[4] || vid[1]) && <figcaption className="text-xs text-[#19324A]/55 mt-2 text-center">{inline(vid[4] || vid[1])}</figcaption>}
          </figure>
        );
        const img = b.match(/^!\[([^\]]*)\]\((\S+)\)(?:\n([\s\S]*))?$/);
        if (img) return (
          <figure key={i} className="my-2" data-testid={`rich-image-${i}`}>
            <img src={img[2]} alt={img[1]} loading="lazy" className="w-full rounded-3xl soft-shadow" />
            {(img[3] || img[1]) && <figcaption className="text-xs text-[#19324A]/55 mt-2 text-center">{inline(img[3] || img[1])}</figcaption>}
          </figure>
        );
        if (b.startsWith("## ")) return <h2 key={i} className="font-display font-extrabold text-2xl text-[#19324A] pt-2">{inline(b.slice(3))}</h2>;
        if (b.startsWith("# ")) return <h2 key={i} className="font-display font-extrabold text-3xl text-[#19324A] pt-2">{inline(b.slice(2))}</h2>;
        if (b.startsWith("### ")) return <h3 key={i} className="font-display font-bold text-lg md:text-xl text-[#19324A]/85">{inline(b.slice(4))}</h3>;
        if (b.startsWith("> ")) return <blockquote key={i} className="border-l-4 border-[#FFD93D] pl-4 italic text-[#19324A]/75">{inline(b.slice(2))}</blockquote>;
        if (b.startsWith("---")) return <hr key={i} className="border-[#19324A]/10" />;
        if (/^[-*] /m.test(b)) return (
          <ul key={i} className="list-disc pl-6 space-y-1 text-[#19324A]/80">
            {b.split("\n").map((l, k) => <li key={k}>{inline(l.replace(/^[-*] /, ""))}</li>)}
          </ul>
        );
        return <p key={i} className="text-[#19324A]/80 leading-relaxed text-base sm:text-lg">{b.split("\n").map((l, k) => <React.Fragment key={k}>{k > 0 && <br />}{inline(l)}</React.Fragment>)}</p>;
      })}
    </div>
  );
}
